import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import FunctionsIcon from "@mui/icons-material/Functions";
import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import dashboardService from "../../api/dashboardService";
import ChartCard from "./components/ChartCard";
import InsightsRow from "./components/InsightsRow";
import KpiStrip from "./components/KpiStrip";
import {
  BORDER,
  CHART,
  PIE_COLORS,
  PRIMARY,
  STATUS_COLORS,
  TEXT,
  TEXT_SOFT,
  TYPE_COLORS,
  fmt,
  fmtDate,
  selectSx,
} from "./constants";

const GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  gap: 2,
};

const span = (cols) => ({ gridColumn: { xs: "span 12", md: `span ${cols}` } });

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: `1px solid ${BORDER}`, boxShadow: "0 10px 28px rgba(15,23,42,0.08)" }}>
      {label && (
        <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.5, color: TEXT }}>{label}</Typography>
      )}
      {payload.map((item) => (
        <Typography key={item.dataKey || item.name} sx={{ fontSize: 12, color: TEXT_SOFT }}>
          <Box component="span" sx={{ color: item.color, fontWeight: 700 }}>{item.name}: </Box>
          {fmt(item.value)}
        </Typography>
      ))}
    </Paper>
  );
}

function EmptyChart({ text = "Sin datos en este periodo" }) {
  return (
    <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Typography sx={{ color: TEXT_SOFT, fontSize: 13 }}>{text}</Typography>
    </Box>
  );
}

function RankList({ rows, valueKey, nameKey = "name", suffix = "" }) {
  if (!rows?.length) {
    return <EmptyChart />;
  }
  const max = Math.max(...rows.map((r) => r[valueKey] || 0), 1);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, overflowY: "auto", height: "100%", pr: 0.5 }}>
      {rows.map((row, i) => (
        <Box key={row.technicianId || row.clientId || row.name || i}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.4 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT, minWidth: 0 }} noWrap>
              {i + 1}. {row[nameKey]}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: PRIMARY, flexShrink: 0 }}>
              {fmt(row[valueKey])}{suffix}
            </Typography>
          </Box>
          <Box sx={{ height: 6, bgcolor: "#eef2f7", borderRadius: 99 }}>
            <Box
              sx={{
                width: `${((row[valueKey] || 0) / max) * 100}%`,
                height: "100%",
                borderRadius: 99,
                bgcolor: PRIMARY,
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function Analisis() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("all");
  const [techScope, setTechScope] = useState("month");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await dashboardService.getExecutiveOverview();
        if (!alive) return;
        setData(res.data || res);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.message || "No se pudo cargar el centro de datos.");
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const years = useMemo(() => {
    const set = new Set((data?.timeline || []).map((m) => m.year));
    return [...set].sort((a, b) => b - a);
  }, [data]);

  const timeline = useMemo(() => {
    const rows = data?.timeline || [];
    if (period === "all") return rows;
    return rows.filter((m) => String(m.year) === String(period));
  }, [data, period]);

  const mixType = data?.mix?.byType || [];
  const mixStatus = (data?.mix?.byStatus || []).filter((s) => s.count > 0);
  const mixFrequency = data?.mix?.byFrequency || [];
  const mixCity = data?.mix?.byCity || [];
  const qualityMonth = data?.quality?.byMonth || [];
  const clientPie = useMemo(() => {
    const rows = data?.clients?.branchesByClient || [];
    const top = rows.slice(0, 8);
    const rest = rows.slice(8).reduce((acc, row) => acc + (row.value || 0), 0);
    return rest > 0 ? [...top, { name: "Otros", value: rest }] : top;
  }, [data]);
  const techRows = techScope === "month"
    ? data?.technicians?.topThisMonth || []
    : data?.technicians?.topAllTime || [];

  const periodLabel = period === "all" ? "desde el primer servicio" : `año ${period}`;

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 360 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pb: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: "#eef5fd",
              color: PRIMARY,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FunctionsIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: TEXT, letterSpacing: "-0.02em" }}>
              Centro de datos
            </Typography>
            <Typography variant="body2" sx={{ color: TEXT_SOFT }}>
              Indicadores para dirección · primer servicio {fmtDate(data.firstServiceDate)} · actualizado {fmtDate(data.generatedAt)}
            </Typography>
          </Box>
        </Stack>

        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
          <Select value={period} onChange={(e) => setPeriod(e.target.value)} sx={selectSx}>
            <MenuItem value="all">Histórico completo</MenuItem>
            {years.map((year) => (
              <MenuItem key={year} value={String(year)}>{year}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <KpiStrip kpis={data.kpis} />
      <InsightsRow highlights={data.highlights} />

      <Box sx={GRID}>
        <Box sx={span(12)}>
          <ChartCard
            title="Servicios por mes"
            subtitle={`Volumen operativo ${periodLabel}, con técnicos en campo`}
            height={340}
          >
            {timeline.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={timeline} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: TEXT_SOFT }}
                    interval={timeline.length > 18 ? "preserveStartEnd" : 0}
                    angle={timeline.length > 10 ? -35 : 0}
                    textAnchor={timeline.length > 10 ? "end" : "middle"}
                    height={timeline.length > 10 ? 58 : 28}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: TEXT_SOFT }} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: TEXT_SOFT }} allowDecimals={false} />
                  <Tooltip content={<ChartTip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="services" name="Servicios" fill={CHART.services} radius={[4, 4, 0, 0]} />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="techniciansWorking"
                    name="Técnicos trabajando"
                    stroke={CHART.technicians}
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                  />
                  {timeline.length > 16 && <Brush dataKey="label" height={22} stroke={PRIMARY} />}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(7)}>
          <ChartCard
            title="Cierre mensual"
            subtitle="Completados, pendientes y cancelados"
            height={300}
          >
            {timeline.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeline} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                  <XAxis
                    dataKey="monthName"
                    tick={{ fontSize: 11, fill: TEXT_SOFT }}
                    interval={timeline.length > 18 ? "preserveStartEnd" : 0}
                  />
                  <YAxis tick={{ fontSize: 11, fill: TEXT_SOFT }} allowDecimals={false} />
                  <Tooltip content={<ChartTip />} />
                  <Legend />
                  <Bar dataKey="completed" name="Completados" stackId="a" fill={CHART.completed} />
                  <Bar dataKey="pending" name="Abiertos" stackId="a" fill={CHART.pending} />
                  <Bar dataKey="cancelled" name="Cancelados" stackId="a" fill={CHART.cancelled} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(5)}>
          <ChartCard
            title="Técnicos trabajando cada mes"
            subtitle="Personas distintas en campo y carga media"
            height={300}
          >
            {timeline.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={timeline} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                  <XAxis dataKey="monthName" tick={{ fontSize: 11, fill: TEXT_SOFT }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: TEXT_SOFT }} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: TEXT_SOFT }} />
                  <Tooltip content={<ChartTip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="techniciansWorking" name="Técnicos" fill={CHART.technicians} radius={[4, 4, 0, 0]} />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="loadPerTech"
                    name="Servicios por técnico"
                    stroke={CHART.load}
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(4)}>
          <ChartCard title="Mix de servicios" subtitle="Tipo de trabajo en la cartera" height={280}>
            {mixType.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={mixType}
                  layout="vertical"
                  margin={{ top: 4, right: 16, left: 4, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: TEXT_SOFT }} allowDecimals={false} />
                  <YAxis type="category" dataKey="label" width={118} tick={{ fontSize: 11, fill: TEXT }} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" name="Servicios" radius={[0, 4, 4, 0]}>
                    {mixType.map((entry, i) => (
                      <Cell key={entry.type} fill={TYPE_COLORS[entry.type] || PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(4)}>
          <ChartCard title="Estado operativo" subtitle="Cómo está hoy la cartera de servicios" height={280}>
            {mixStatus.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={mixStatus} dataKey="count" nameKey="label" innerRadius="58%" outerRadius="82%" paddingAngle={2}>
                    {mixStatus.map((entry) => (
                      <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || PRIMARY} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(4)}>
          <ChartCard title="Recurrencia" subtitle="Cartera mensual vs servicios únicos" height={280}>
            {mixFrequency.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mixFrequency} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: TEXT_SOFT }} />
                  <YAxis tick={{ fontSize: 11, fill: TEXT_SOFT }} allowDecimals={false} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" name="Servicios" fill={PRIMARY} radius={[4, 4, 0, 0]}>
                    {mixFrequency.map((entry, i) => (
                      <Cell key={entry.frequency} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(6)}>
          <ChartCard
            title="Técnicos con más carga"
            subtitle={techScope === "month" ? "Asignaciones de este mes" : "Histórico de asignaciones"}
            height={300}
            extra={
              <Box sx={{ display: "flex", gap: 0.5, p: 0.4, bgcolor: "#f3f4f6", borderRadius: "8px" }}>
                {[
                  { id: "month", label: "Mes" },
                  { id: "all", label: "Histórico" },
                ].map((opt) => (
                  <Box
                    key={opt.id}
                    component="button"
                    onClick={() => setTechScope(opt.id)}
                    sx={{
                      border: 0,
                      px: 1.1,
                      py: 0.4,
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: techScope === opt.id ? "#fff" : "transparent",
                      color: techScope === opt.id ? PRIMARY : TEXT_SOFT,
                      boxShadow: techScope === opt.id ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                    }}
                  >
                    {opt.label}
                  </Box>
                ))}
              </Box>
            }
          >
            <RankList rows={techRows} valueKey="services" />
          </ChartCard>
        </Box>

        <Box sx={span(6)}>
          <ChartCard title="Clientes con más servicios" subtitle="Volumen y sucursales de la cuenta" height={300}>
            <RankList rows={data.clients.top} valueKey="services" />
          </ChartCard>
        </Box>

        <Box sx={span(7)}>
          <ChartCard
            title="Distribución de sucursales"
            subtitle="Cobertura por cliente (ALSEA agrupado)"
            height={300}
          >
            {clientPie.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={clientPie}
                  layout="vertical"
                  margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: TEXT_SOFT }} allowDecimals={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={110}
                    tick={{ fontSize: 11, fill: TEXT }}
                  />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="value" name="Sucursales" fill={PRIMARY} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(5)}>
          <ChartCard title="Cobertura geográfica" subtitle="Sucursales por ciudad" height={300}>
            {mixCity.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={mixCity} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                  <XAxis dataKey="city" tick={{ fontSize: 11, fill: TEXT_SOFT }} interval={0} angle={-25} textAnchor="end" height={64} />
                  <YAxis tick={{ fontSize: 11, fill: TEXT_SOFT }} allowDecimals={false} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" name="Sucursales" fill="#0369a1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(6)}>
          <ChartCard
            title="Nivel de plaga y reportes"
            subtitle="Evolución del riesgo en campo"
            height={300}
          >
            {timeline.every((m) => m.reports === 0) ? (
              <EmptyChart text="Aún no hay reportes de plaga en el periodo" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={timeline} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                  <XAxis dataKey="monthName" tick={{ fontSize: 11, fill: TEXT_SOFT }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11, fill: TEXT_SOFT }} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: TEXT_SOFT }} />
                  <Tooltip content={<ChartTip />} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="reports" name="Reportes" fill={CHART.pest} radius={[4, 4, 0, 0]} />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="avgPestLevel"
                    name="Nivel promedio"
                    stroke="#7c2d12"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(6)}>
          <ChartCard
            title="Calidad y puntualidad"
            subtitle="Promedio de evaluaciones del área de calidad"
            height={300}
          >
            {qualityMonth.length === 0 ? (
              <EmptyChart text="Aún no hay evaluaciones de calidad" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={qualityMonth} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: TEXT_SOFT }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: TEXT_SOFT }} />
                  <Tooltip content={<ChartTip />} />
                  <Legend />
                  <Line type="monotone" dataKey="avgQuality" name="Calidad" stroke={CHART.quality} strokeWidth={2.5} />
                  <Line type="monotone" dataKey="avgPunctuality" name="Puntualidad" stroke={CHART.punctuality} strokeWidth={2.5} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(4)}>
          <ChartCard
            title="Técnicos activos sin carga"
            subtitle={`Este mes · ${fmt(data.technicians.idleThisMonth.length)} personas`}
            height={280}
          >
            {data.technicians.idleThisMonth.length === 0 ? (
              <EmptyChart text="Toda la plantilla activa tiene servicios" />
            ) : (
              <Box sx={{ overflowY: "auto", height: "100%", pr: 0.5 }}>
                {data.technicians.idleThisMonth.map((tech) => (
                  <Box
                    key={tech.technicianId}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 1,
                      borderBottom: `1px solid ${BORDER}`,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT }}>{tech.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: TEXT_SOFT }}>{tech.zone || "Sin zona"}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(4)}>
          <ChartCard title="Clientes con plaga activa" subtitle="Sucursales con último reporte ≥ 1" height={280}>
            <RankList rows={data.hotClients} valueKey="infestedBranches" suffix=" suc." />
          </ChartCard>
        </Box>

        <Box sx={span(4)}>
          <ChartCard
            title="Inventario bajo mínimo"
            subtitle="Ítems que requieren reposición"
            height={280}
          >
            {data.inventory.lowStock.length === 0 ? (
              <EmptyChart text="No hay existencias bajo mínimo" />
            ) : (
              <Box sx={{ overflowY: "auto", height: "100%", pr: 0.5 }}>
                {data.inventory.lowStock.map((item, i) => (
                  <Box
                    key={`${item.name}-${i}`}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      py: 1,
                      borderBottom: `1px solid ${BORDER}`,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT }} noWrap>
                        {item.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: TEXT_SOFT }}>{item.location}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#d97706", flexShrink: 0 }}>
                      {fmt(item.quantity)} / {fmt(item.minStock)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </ChartCard>
        </Box>

        <Box sx={span(12)}>
          <ChartCard
            title="Certificados emitidos"
            subtitle="Documentos de servicio por mes"
            height={240}
          >
            {timeline.every((m) => m.certificates === 0) ? (
              <EmptyChart text="Aún no hay certificados emitidos en el periodo" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeline} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: TEXT_SOFT }} interval={timeline.length > 18 ? "preserveStartEnd" : 0} />
                  <YAxis tick={{ fontSize: 11, fill: TEXT_SOFT }} allowDecimals={false} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="certificates" name="Certificados" fill={CHART.certificates} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Box>
      </Box>
    </Box>
  );
}
